import React, { useState, useEffect, useRef } from 'react';
import { 
  Bookmark, 
  Share2, 
  Star, 
  MapPin, 
  Calendar,
  Users,
  Bus,
  UtensilsCrossed,
  Edit,
  Trash2,
  Navigation,
  X,
  Heart,
  CheckCircle,
  Clock,
  Phone,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ValidationBadge, CampValidationWarning } from '@/components/ValidationBadge';
import { getCategoryEmoji } from '@/utils/emojiHelpers';
import { CommunityBadge, CommunityFieldIndicator } from '@/components/camps/CommunityBadge';
import { CompactDataQualityIndicator } from '@/components/camps/DataQualityBar';
import { useThemeSettings } from '@/contexts/ThemeContext';
import { formatCampDisplayName } from '@/utils/campNameFormatter';

// Simple child avatar component
const ChildAvatar = ({ child, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  };
  
  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };
  
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '??';
    return name
      .trim()
      .split(' ')
      .filter(n => n.length > 0)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };
  
  const photoUrl = child?.photoUrl || child?.photo_url;
  const initials = getInitials(child?.childName || child?.name);
  const paddingSize = size === 'sm' ? 'p-0.5' : 'p-0.5';
  
  const gradientStyle = {
    background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
  };
  
  // Check for dark mode - we'll use a simpler approach that works client-side
  const isDark = typeof document !== 'undefined' && document?.documentElement?.classList?.contains('dark') || false;
  
  if (photoUrl) {
    return (
      <div className={`rounded-full ${paddingSize}`} style={gradientStyle}>
        <div className={`${sizeClasses[size]} rounded-full overflow-hidden ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
          <img
            src={photoUrl}
            alt={child?.childName || child?.name || 'Child'}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent) {
                const fallback = document.createElement('div');
                fallback.className = `w-full h-full rounded-full flex items-center justify-center`;
                fallback.innerHTML = `<span class="${textSizes[size]} font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}">${initials}</span>`;
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className={`rounded-full ${paddingSize}`} style={gradientStyle}>
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
        <span className={`${textSizes[size]} font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{initials}</span>
      </div>
    </div>
  );
};

/**
 * Unified Program Card Component
 * Supports multiple view modes and responsive layouts
 */
const UnifiedProgramCard = ({
  program,
  viewMode = 'discovery', // 'discovery' | 'saved' | 'management' | 'perfectMatch'
  variant = 'vertical', // 'vertical' | 'horizontal' | 'compact'
  isSaved = false,
  onSave,
  onClick,
  onView,
  onEdit,
  onDelete,
  onDirections,
  onCancelBooking,
  onWhosIn,
  onDismiss,
  children = [], // Array of child objects with name and photo_url
  matchData = null, // { score, confidence, reason }
  isDismissed = false,
  isSelected = false,
  isHovered = false,
  onMouseEnter,
  onMouseLeave,
  hideCategoryBadge = false,
  statusInlineWithTitle = false,
  uniformHeight = false,
  flatListing = false // When true, no card container (e.g. Email Scout list inside section card)
}) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  
  // Move debug logs to useEffect to avoid React rendering issues
  useEffect(() => {
    if (program) {
      console.log('🔍 UnifiedProgramCard RENDERED', {
        programName: program?.name || program?.title,
        programId: program?.id || program?.place_id,
        viewMode,
        variant,
        rating: program?.rating,
        reviewCount: program?.reviewCount,
        user_ratings_total: program?.user_ratings_total,
        reviewsLength: program?.reviews?.length,
        category: program?.category,
        primary_category: program?.primary_category,
        image_url: program?.image_url,
        hasPhotos: Array.isArray(program?.photos) && program?.photos?.length > 0,
      });
    }
  }, [program, viewMode, variant]);

  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const errorTimeoutRef = useRef(null);

  // Helper to validate if a string is a valid image URL
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return false;
    // Reject if it looks like a Place ID (starts with ChIJ, GhIJ, EhIJ)
    if (/^(ChIJ|GhIJ|EhIJ)/.test(trimmed)) {
      console.warn('⚠️ UnifiedProgramCard: Rejected Place ID as image URL:', trimmed);
      return false;
    }
    try {
      const urlObj = new URL(trimmed);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:' || urlObj.protocol === 'data:';
    } catch {
      return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
    }
  };

  // Helper to safely extract organization/vicinity as string
  const getOrganizationString = () => {
    const org = program?.organization || program?.vicinity || program?.formatted_address || program?.general_location;
    if (typeof org === 'string') return org;
    if (org && typeof org === 'object') {
      // If it's an object, try to extract address-like properties
      return org.formatted_address || org.address || org.name || org.text || 'Organization';
    }
    return 'Organization';
  };

  // Helper to safely extract location string (location/address fields may be objects from API)
  const getLocationString = () => {
    const asString = (v) => {
      if (v == null) return null;
      if (typeof v === 'string') return v.trim() || null;
      if (typeof v === 'object' && v !== null) {
        const s = v.formatted_address || v.address || v.name || v.text;
        return typeof s === 'string' && s.trim() ? s.trim() : null;
      }
      return null;
    };
    return asString(program?.location)
      || asString(program?.program_data?.location)
      || asString(program?.formatted_address)
      || asString(program?.address)
      || asString(program?.vicinity)
      || asString(program?.general_location)
      || asString(program?.program_data?.general_location)
      || asString(program?.program_data?.formatted_address)
      || asString(program?.program_data?.address)
      || null;
  };

  // Get photo URL with fallbacks - PRIORITIZE DATABASE FIELDS FIRST
  const getPhotoUrl = () => {
    
    // PRIORITY 1: Check direct database image fields FIRST (these are stored URLs)
    const imageFields = ['image_url', 'image', 'photo_url', 'photo', 'thumbnail', 'thumbnail_url'];
    for (const field of imageFields) {
      if (isValidImageUrl(program[field])) {
        console.log(`✅ getPhotoUrl found direct database field ${field}:`, program[field]);
        return program[field];
      }
    }
    
    // PRIORITY 2: Check program_data for nested image fields
    if (program.program_data) {
      for (const field of imageFields) {
        if (isValidImageUrl(program.program_data[field])) {
          console.log(`✅ getPhotoUrl found nested program_data.${field}:`, program.program_data[field]);
          return program.program_data[field];
        }
      }
    }
    
    // PRIORITY 3: Check photos array (Google Places format) - only if no database URLs found
    if (Array.isArray(program.photos) && program.photos.length > 0) {
      const firstPhoto = program.photos[0];
      
      // Check photoUrl property (used by transformPlaces) - this might be a stored URL
      if (firstPhoto?.photoUrl && isValidImageUrl(firstPhoto.photoUrl)) {
        console.log('✅ getPhotoUrl found photo.photoUrl:', firstPhoto.photoUrl);
        return firstPhoto.photoUrl;
      }
      
      // String URL in photos array
      if (typeof firstPhoto === 'string' && isValidImageUrl(firstPhoto)) {
        console.log('✅ getPhotoUrl found string URL in photos array:', firstPhoto);
        return firstPhoto;
      }
      
      // Photo object with url property
      if (firstPhoto?.url && isValidImageUrl(firstPhoto.url)) {
        console.log('✅ getPhotoUrl found photo.url:', firstPhoto.url);
        return firstPhoto.url;
      }
      
      // Google Places Photo object with getUrl method
      if (firstPhoto?.getUrl && typeof firstPhoto.getUrl === 'function') {
        try {
          const url = firstPhoto.getUrl({ maxWidth: 800, maxHeight: 600 });
          if (isValidImageUrl(url)) {
            console.log('✅ getPhotoUrl found Google Places getUrl():', url);
            return url;
          }
        } catch (error) {
          console.warn('Error calling getUrl:', error);
        }
      }
      
      // Photo object with name (NEW Places API v1 format) - only construct if valid
      if (firstPhoto?.name && typeof firstPhoto.name === 'string' && firstPhoto.name.trim() !== '') {
        // Validate photo.name format - must start with "places/" and contain "/photos/"
        if (firstPhoto.name.startsWith('places/') && firstPhoto.name.includes('/photos/')) {
          const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
          if (apiKey && apiKey.trim() !== '') {
            const constructedUrl = `https://places.googleapis.com/v1/${firstPhoto.name}/media?maxWidthPx=800&key=${apiKey}`;
            return constructedUrl;
          } else {
            console.warn('⚠️ UnifiedProgramCard: Missing or empty API key for photo URL construction');
          }
        } else {
          console.warn('⚠️ UnifiedProgramCard: Invalid photo.name format (expected "places/.../photos/..."):', {
            photoName: firstPhoto.name,
            programName: program?.name || program?.title,
            placeId: program?.id || program?.place_id
          });
        }
      }
      
      // Fallback: Photo object with photo_reference (legacy format)
      if (firstPhoto?.photo_reference && typeof firstPhoto.photo_reference === 'string') {
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (apiKey) {
          const constructedUrl = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photoreference=${firstPhoto.photo_reference}&key=${apiKey}`;
          console.log('⚠️ getPhotoUrl using legacy format from photo_reference:', constructedUrl);
          return constructedUrl;
        }
      }
    }
    
    console.log('❌ getPhotoUrl - NO IMAGE URL FOUND');
    return null;
  };

  const photoUrl = program ? getPhotoUrl() : null;
  let programEmoji = '📋';
  if (program) {
    try {
      programEmoji = program.emoji || getCategoryEmoji(program);
    } catch (_) {
      programEmoji = '📋';
    }
  }
  
  // Reset loading state when photoUrl changes
  useEffect(() => {
    // Clear any pending error timeouts
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    
    if (photoUrl) {
      setImageLoading(true);
      setImageError(false);
    } else {
      setImageLoading(false);
    }
    
    // Cleanup on unmount
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [photoUrl]);

  // Guard: do not render if program is missing (after all hooks to satisfy rules of hooks)
  if (!program) {
    return null;
  }

  // Get category display name and color
  const getCategoryDisplayName = (category) => {
    if (!category) return 'Program';
    const categoryLower = typeof category === 'string' ? category.toLowerCase() : '';
    
    // Handle case-insensitive matching with variations
    const categoryMap = {
      'summer camp': 'Camp',
      'camp': 'Camp',
      'camps': 'Camp',
      'sports & recreation': 'Sports',
      'sports': 'Sports',
      'sport': 'Sports',
      'stem & education': 'STEM',
      'stem': 'STEM',
      'education': 'Education',
      'school': 'Education',
      'arts & culture': 'Arts',
      'arts': 'Arts',
      'art': 'Arts',
      'outdoor & adventure': 'Outdoor',
      'outdoor': 'Outdoor',
      'adventure': 'Adventure',
      'community programs': 'Community',
      'community': 'Community',
      'enrichment': 'Enrichment',
      'general youth program': 'Program',
      'general': 'Program',
      'youth program': 'Program',
      'program': 'Program'
    };
    
    // Check exact match first (case-insensitive)
    if (categoryMap[categoryLower]) {
      return categoryMap[categoryLower];
    }
    
    // Check if category contains any key word
    for (const [key, value] of Object.entries(categoryMap)) {
      if (categoryLower.includes(key)) {
        return value;
      }
    }
    
    // Fallback to capitalized first letter of original
    return typeof category === 'string' ? category.charAt(0).toUpperCase() + category.slice(1).toLowerCase() : 'Program';
  };

  // Get camp type badge (Day Camp, Overnight Camp, or Day / Overnight)
  const getCampTypeBadge = () => {
    const campType = program?.camp_type || program?.camp?.camp_type;
    if (!campType) return null;
    
    if (campType === 'both') {
      return { label: 'Day / Overnight', className: 'bg-purple-100 text-purple-800' };
    }
    
    if (campType === 'overnight_camp' || campType === 'overnight') {
      return { label: 'Overnight Camp', className: 'bg-indigo-100 text-indigo-800' };
    }
    
    // Only show if camp_type exists (don't default to "Day Camp" if field is missing)
    return { label: 'Day Camp', className: 'bg-blue-100 text-blue-800' };
  };
  
  const getCategoryColor = (category) => {
    const displayName = getCategoryDisplayName(category);
    // Always return colorful badges, never grey
    switch (displayName) {
      case 'Camp':
        return 'bg-purple-100 text-brand-800';
      case 'Sports':
        return 'bg-orange-100 text-orange-800';
      case 'STEM':
        return 'bg-blue-100 text-blue-800';
      case 'Arts':
        return 'bg-pink-100 text-pink-800';
      case 'Outdoor':
      case 'Adventure':
        return 'bg-green-100 text-green-800';
      case 'Community':
        return 'bg-teal-100 text-teal-800';
      case 'Enrichment':
        return 'bg-indigo-100 text-indigo-800';
      case 'Education':
        return 'bg-blue-100 text-blue-800';
      case 'General':
      case 'general':
        // Use a default colorful badge instead of grey
        return 'bg-slate-100 text-slate-800';
      default:
        // Default to a colorful badge (slate) instead of grey
        return 'bg-slate-100 text-slate-800';
    }
  };

  // Get status badges
  const renderStatusBadges = (options = {}) => {
    let wrapperClass = 'flex flex-wrap gap-1';
    let variant = 'default';

    if (typeof options === 'string') {
      wrapperClass = options;
    } else if (options && typeof options === 'object') {
      wrapperClass = options.wrapperClass || wrapperClass;
      variant = options.variant || variant;
    }

    if (!program.status || (Array.isArray(program.status) && program.status.length === 0)) return null;
    
    const statusArray = Array.isArray(program.status) ? program.status : [program.status];
    const statusMap = {
      favorite: { 
        label: 'Favorite', 
        color: 'bg-purple-100 text-brand-800',
        icon: Heart
      },
      booked: { 
        label: 'Booked', 
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      considering: { 
        label: 'Considering', 
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      attended: { 
        label: 'Attended', 
        color: 'bg-blue-100 text-blue-800',
        icon: Calendar
      },
      saved: { 
        label: 'Saved', 
        color: 'bg-orange-100 text-orange-800',
        icon: Bookmark
      },
      recommended: { 
        label: 'Recommended', 
        color: 'bg-pink-100 text-pink-800',
        icon: Star
      }
    };

    const filteredStatuses = statusArray.filter(status => status !== 'saved');
    if (filteredStatuses.length === 0) return null;

    // Match the style of StatusSummaryCards filters
    return (
      <div className={`${wrapperClass} gap-1.5`}>
        {filteredStatuses.map((status) => {
          const statusInfo = statusMap[status];
          if (!statusInfo) return null;
          
          const IconComponent = statusInfo.icon;
          
          return (
            <span
              key={status}
              className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border shadow-sm bg-gray-100 text-gray-700 border-gray-200"
            >
              <IconComponent className="h-3.5 w-3.5 text-gray-600" />
              <span>{statusInfo.label}</span>
            </span>
          );
        })}
      </div>
    );
  };

  const renderActionButtons = (wrapperClass = 'flex items-center gap-1.5 ml-auto flex-wrap') => {
    const buttons = [];

    if (onEdit) {
      buttons.push(
        <Button
          key="edit"
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(program);
          }}
          className="h-7 md:h-8 px-2 md:px-3 text-xs border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 gap-1 bg-white/50"
        >
          <Edit className="h-3 w-3" />
          <span>Edit</span>
        </Button>
      );
    }

    if (onDelete) {
      buttons.push(
        <Button
          key="delete"
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(program);
          }}
          className="h-7 md:h-8 px-2 md:px-3 text-xs border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 gap-1 bg-white/50"
        >
          <Trash2 className="h-3 w-3" />
          <span>Delete</span>
        </Button>
      );
    }

    // Directions button removed - no longer needed on My Programs page

    if (buttons.length === 0) return null;

    return (
      <div className={wrapperClass}>
        {buttons}
      </div>
    );
  };

  // Get source badge (database source intentionally not shown on cards)
  const getSourceBadge = () => {
    const sourceMap = {
      'ai_discovered': { label: 'AI DISCOVERED', color: 'bg-blue-100 text-blue-800' },
      'search': { label: 'SEARCH RESULT', color: 'bg-green-100 text-green-800' },
      'google': { label: 'GOOGLE PLACES', color: 'bg-orange-100 text-orange-800' }
    };
    
    if (!program.source || program.source === 'manual_entry' || program.source === 'manual' || program.source === 'database') {
      return null;
    }
    
    const source = sourceMap[program.source];
    if (!source) return null;
    
    return (
      <Badge className={`text-xs font-medium ${source.color}`}>
        {source.label}
      </Badge>
    );
  };

  // Format date range
  const formatDateRange = () => {
    const startDate = program.campStartDate || program.start_date || program.dates;
    const endDate = program.campEndDate || program.end_date;
    
    if (!startDate) return 'Dates TBD';
    
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateStr);
    };
    
    const start = parseDate(startDate);
    const end = endDate ? parseDate(endDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    if (end && start.getTime() !== end.getTime()) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    
    return formatDate(start);
  };

  // Format price - prioritize user-contributed data
  const formatPrice = () => {
    const isBooked = program.status && (Array.isArray(program.status) ? program.status.includes('booked') : program.status === 'booked');
    
    // Priority 1: User-contributed cost details
    if (program.user_cost_details?.amount) {
      const amount = program.user_cost_details.amount;
      const period = program.user_cost_details.period || 'week';
      return <div className={`text-sm md:text-base font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>${amount}/{period}</div>;
    }
    
    if (isBooked && program.regularPrice) {
      return <div className={`text-sm md:text-base font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>${program.regularPrice}</div>;
    }
    
    if (program.earlyBirdPrice && program.regularPrice && program.earlyBirdPrice < program.regularPrice) {
      return (
        <div className="text-right">
          <div className={`text-sm md:text-base font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>${program.earlyBirdPrice}</div>
          <div className="text-xs text-gray-500 line-through">${program.regularPrice}</div>
        </div>
      );
    }
    
    if (program.regularPrice) {
      return <div className={`text-sm md:text-base font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>${program.regularPrice}</div>;
    }
    
    if (program.price && typeof program.price === 'string' && !program.price.includes('Contact for pricing')) {
      return program.price;
    }
    
    return null;
  };

  // Get display cost (for string display)
  const getDisplayCost = () => {
    // Priority 1: User-contributed cost details
    if (program.user_cost_details?.amount) {
      const amount = program.user_cost_details.amount;
      const period = program.user_cost_details.period || 'week';
      return `$${amount}/${period}`;
    }
    
    // Fallback to other price fields
    if (program.regularPrice) {
      return `$${program.regularPrice}`;
    }
    if (program.price && typeof program.price === 'string' && !program.price.includes('Contact for pricing')) {
      return program.price;
    }
    return null;
  };

  // Get display times - prioritize user-contributed data
  const getDisplayTimes = () => {
    if (program.user_session_start_time && program.user_session_end_time) {
      return `${program.user_session_start_time} - ${program.user_session_end_time}`;
    }
    return null;
  };

  // Get display dates - prioritize user-contributed data
  const getDisplayDates = () => {
    const startDate = program.user_camp_start_date || program.campStartDate || program.start_date || program.dates;
    const endDate = program.user_camp_end_date || program.campEndDate || program.end_date;
    
    if (!startDate) return null;
    
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return dateStr;
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d;
    };
    
    const start = parseDate(startDate);
    const end = endDate ? parseDate(endDate) : null;
    
    const formatDate = (date) => {
      if (!date) return null;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // If parsing failed, hide dates entirely
    if (!start) return null;

    if (end && start.getTime() !== end.getTime()) {
      const startStr = formatDate(start);
      const endStr = formatDate(end);
      if (startStr && endStr) {
        return `${startStr} - ${endStr}`;
      }
      return startStr || endStr || null;
    }
    
    return formatDate(start);
  };

  // Get child initials and photo
  const findChildPhoto = (childName, childrenList) => {
    if (!childName || !childrenList || childrenList.length === 0) return null;
    const normalize = (name) => name?.trim().toLowerCase().replace(/\s+/g, ' ') || '';
    const normalizedInput = normalize(childName);
    if (!normalizedInput) return null;
    
    // First try exact ID match
    let child = childrenList.find(c => c?.id && (c.id === childName || c.id.toString() === childName.toString()));
    
    // Then try exact name match (case-insensitive)
    if (!child) {
      child = childrenList.find(c => {
        const normalizedChildName = normalize(c?.name);
        return normalizedChildName === normalizedInput;
      });
    }
    
    // Then try partial match (first name)
    if (!child && normalizedInput.includes(' ')) {
      const firstName = normalizedInput.split(' ')[0];
      child = childrenList.find(c => {
        const normalizedChildName = normalize(c?.name);
        return normalizedChildName.split(' ')[0] === firstName || normalizedChildName === firstName;
      });
    }
    
    // Also try matching if childName contains the child's name or vice versa
    if (!child) {
      child = childrenList.find(c => {
        const normalizedChildName = normalize(c?.name);
        return normalizedChildName.includes(normalizedInput) || normalizedInput.includes(normalizedChildName);
      });
    }
    
    // Return photo_url if available, otherwise null
    return child?.photo_url || null;
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string' || name.trim().length === 0) return '??';
    const trimmed = name.trim();
    if (trimmed.toLowerCase() === 'child' || trimmed.toLowerCase() === 'children') return '??';
    return trimmed.split(' ').filter(n => n.length > 0).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getColorClass = (name) => {
    const colors = ['bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'];
    if (!name || typeof name !== 'string' || name.trim().length === 0) return colors[0];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Handle actions
  const handleSaveClick = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    console.log('🔖 Save button clicked:', { 
      programId: program?.id || program?.place_id, 
      programName: program?.name || program?.title,
      hasOnSave: !!onSave 
    });
    if (onSave) {
      onSave(program);
    } else {
      console.warn('⚠️ Save button clicked but onSave handler is not provided');
    }
  };

  const handleShareClick = (e) => {
    e?.stopPropagation();
    if (navigator.share) {
      const title = formatCampDisplayName(program);
      const org = program.organization || program.vicinity || program.formatted_address || 'Local Organization';
      navigator.share({
        title,
        text: `Check out ${title} at ${org}`,
        url: window.location.href
      });
    }
  };

  // Responsive image/emoji container
  const ImageContainer = ({ className = '' }) => {
    const imageClasses = {
      discovery: 'h-40 md:h-44 lg:h-48',
      saved: 'h-32 md:h-36 lg:h-40',
      management: 'h-32 md:h-36 lg:h-40',
      perfectMatch: 'h-12 md:h-14 lg:h-16'
    };
    
    const emojiSizes = {
      discovery: 'text-4xl md:text-5xl lg:text-6xl',
      saved: 'text-3xl md:text-4xl lg:text-5xl',
      management: 'text-3xl md:text-4xl lg:text-5xl',
      perfectMatch: 'text-xl md:text-2xl lg:text-3xl'
    };

    const heightClass = imageClasses[viewMode] || imageClasses.discovery;
    const emojiSizeClass = emojiSizes[viewMode] || emojiSizes.discovery;

    if (photoUrl) {
      return (
        <div className={`relative ${heightClass} ${className}`}>
          {/* Show image if loading or no error, hide if error after timeout */}
          {(!imageError || imageLoading) && (
            <img
              src={photoUrl}
              alt={formatCampDisplayName(program)}
              className="w-full h-full object-cover"
              onLoad={() => {
                setImageLoading(false);
                setImageError(false);
              }}
              onError={(e) => {
                // Give image time to load before showing fallback
                // Only set error after a delay to allow for slow loading
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                errorTimeoutRef.current = setTimeout(() => {
                  if (import.meta.env.DEV) {
                    console.warn('⚠️ UnifiedProgramCard: Image failed to load after timeout:', {
                      programName: program?.name || program?.title,
                      programId: program?.id || program?.place_id,
                      photoUrl: photoUrl
                    });
                  }
                  setImageError(true);
                  setImageLoading(false);
                  errorTimeoutRef.current = null;
                }, 2000); // Give image 2 seconds to load before showing fallback
                e.preventDefault();
              }}
            />
          )}
          {/* Emoji fallback - show if error occurred (after timeout) or if no photoUrl */}
          <div className={`absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center ${imageError && !imageLoading ? '' : 'hidden'}`}>
            <div className={emojiSizeClass}>{programEmoji}</div>
          </div>
        </div>
      );
    }

    return (
      <div className={`${heightClass} bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center ${className}`}>
        <div className={emojiSizeClass}>{programEmoji}</div>
      </div>
    );
  };

  // Keyboard accessibility handler
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) {
        onClick(program);
      } else if (onView) {
        onView(program);
      }
    }
  };

  // Discovery View
  if (viewMode === 'discovery' && variant === 'vertical') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`
          ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} rounded-lg border ${isDarkTheme ? 'border-slate-700/40' : ''} cursor-pointer transition-all duration-300
          ${isSelected 
            ? 'border-gray-300 shadow-lg' 
            : isHovered
            ? 'border-gray-300 shadow-md'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
          }
          focus:outline-none
        `}
        aria-label={`View details for ${formatCampDisplayName(program)}`}
      >
        <div className="relative">
          <ImageContainer className="rounded-t-lg overflow-hidden" />
          {/* ========== DEBUGGING LOGS - START ========== */}
          {console.log('🖼️ Discovery Image Container:', {
            photoUrl,
            imageError,
            programEmoji,
            willShowImage: !!photoUrl && !imageError,
            willShowEmoji: !photoUrl || imageError
          })}
          {/* ========== DEBUGGING LOGS - END ========== */}
          {onSave && (
            <button
              onClick={handleSaveClick}
              type="button"
              className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-md z-20 pointer-events-auto"
              aria-label={isSaved ? "Unsave program" : "Save program"}
            >
              <Bookmark 
                className={`w-4 h-4 md:w-5 md:h-5 transition-colors ${
                  isSaved ? 'fill-brand-600 text-brand-600' : 'text-gray-700'
                }`}
              />
            </button>
          )}
        </div>

        <div className="p-3 md:p-4">
          <div className="mb-2">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'} text-sm md:text-base line-clamp-3`}>
                  {formatCampDisplayName(program)}
                </h3>
                {program.sessionCount > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {program.sessionCount} {program.sessionCount === 1 ? 'session' : 'sessions'}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* PHASE 4: Show session badge for sessions */}
                {program.isSession && (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                    Session
                  </span>
                )}
                {/* Hide CommunityBadge in discovery mode when it would show "Google Places" */}
                {!(viewMode === 'discovery' && !program.user_contributed && !program.has_community_enhancements) && (
                  <CommunityBadge 
                    camp={program} 
                    size="sm"
                    variant="outline"
                  />
                )}
                <ValidationBadge camp={program} />
              </div>
            </div>
            <p className="text-xs text-gray-600 line-clamp-1">
              {getOrganizationString()}
            </p>
          </div>

              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {program.rating && (
              <div className="flex items-center gap-1 text-xs">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className={`font-medium ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{program.rating}</span>
                {(() => {
                  const reviewCount = program.reviewCount || program.user_ratings_total || program.review_count || program.reviews?.length || 0;
                  // Only show review count if it's greater than 0 (match Search Results format)
                  return reviewCount > 0 ? (
                    <span className="text-gray-500">({reviewCount})</span>
                  ) : null;
                })()}
              </div>
            )}
            {/* Always show category badge, default to 'Program' if no category */}
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColor(program.category || program.primary_category || 'Program')}`}>
              {getCategoryDisplayName(program.category || program.primary_category || 'Program')}
            </span>
            {/* Show camp type badge if camp_type is available */}
            {(() => {
              const campTypeBadge = getCampTypeBadge();
              return campTypeBadge ? (
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${campTypeBadge.className}`}>
                  {campTypeBadge.label}
                </span>
              ) : null;
            })()}
          </div>

          <div className="space-y-2 mb-3 text-xs">
            {program.distance !== null && program.distance !== undefined && parseFloat(program.distance) > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>{parseFloat(program.distance).toFixed(1)} miles away</span>
                </div>
                <button
                  onClick={handleShareClick}
                  className="p-1.5 text-gray-600 hover:text-brand-600 hover:bg-purple-50 rounded-lg transition-colors hidden md:block"
                  aria-label="Share program"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {(program.transportation || program.lunch) && (
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
              {program.transportation && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Bus className="w-4 h-4 text-brand-600" />
                  <span>Transportation</span>
                </div>
              )}
              {program.lunch && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <UtensilsCrossed className="w-4 h-4 text-brand-600" />
                  <span>Lunch Included</span>
                </div>
              )}
            </div>
          )}

          {/* Data Quality Indicator (if community data) */}
          {program.has_community_enhancements && program.data_confidence_score >= 70 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <CompactDataQualityIndicator 
                score={program.data_confidence_score} 
              />
            </div>
          )}

          {getDisplayCost() && (
            <div className="flex items-center gap-1.5">
              <div>
                <div className={`text-xs font-medium ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{getDisplayCost()}</div>
                <div className="text-xs text-gray-500">per week</div>
              </div>
              <CommunityFieldIndicator 
                field="cost_details" 
                camp={program}
                size="sm"
              />
            </div>
          )}
          
          {/* Session Times with Community Indicator */}
          {getDisplayTimes() && (
            <div className="flex items-center gap-2 text-xs text-gray-600 mt-2">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>{getDisplayTimes()}</span>
              <CommunityFieldIndicator 
                field="session_start_time" 
                camp={program}
                size="sm"
              />
            </div>
          )}
          
          {/* Camp Dates with Community Indicator */}
          {getDisplayDates() && (
            <div className="flex items-center gap-2 text-xs text-gray-600 mt-2">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{getDisplayDates()}</span>
              <CommunityFieldIndicator 
                field="camp_start_date" 
                camp={program}
                size="sm"
              />
            </div>
          )}

          <CampValidationWarning camp={program} />
        </div>
      </div>
    );
  }

  // Discovery View - Horizontal List (Discover page)
  if (viewMode === 'discovery' && variant === 'horizontal') {
    const handleCardClick = () => {
      if (onClick) {
        onClick(program);
      } else if (onView) {
        onView(program);
      }
    };

    const reviewCount = program.reviewCount || program.user_ratings_total || program.review_count || program.reviews?.length || program.program_data?.user_ratings_total || program.program_data?.reviewCount || 0;
    const hasRating = program.rating != null && (typeof program.rating === 'number' ? program.rating > 0 : parseFloat(program.rating) > 0);

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`
          relative ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} border ${isDarkTheme ? 'border-slate-700/40' : 'border-gray-200'} rounded-lg cursor-pointer transition-all duration-300
          hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
        `}
        aria-label={`View details for ${formatCampDisplayName(program)}`}
      >
        <div className="p-3 md:p-4">
          <div className="flex items-start gap-2 md:gap-3">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center overflow-hidden relative">
                {photoUrl ? (
                  <>
                    {(!imageError || imageLoading) && (
                      <img
                        src={photoUrl}
                        alt={program.name || program.title}
                        className="w-full h-full object-cover"
                        onLoad={() => {
                          setImageLoading(false);
                          setImageError(false);
                        }}
                        onError={(e) => {
                          if (errorTimeoutRef.current) {
                            clearTimeout(errorTimeoutRef.current);
                          }
                          errorTimeoutRef.current = setTimeout(() => {
                            setImageError(true);
                            setImageLoading(false);
                            errorTimeoutRef.current = null;
                          }, 2000);
                          e.preventDefault();
                        }}
                      />
                    )}
                    <div className={`absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center ${imageError && !imageLoading ? '' : 'hidden'}`}>
                      <div className="text-xl md:text-2xl">{programEmoji}</div>
                    </div>
                  </>
                ) : (
                  <div className="text-xl md:text-2xl">{programEmoji}</div>
                )}
              </div>
              {hasRating && (
                <div className="flex flex-col items-center justify-center gap-0.5 text-xs max-w-[72px] md:max-w-[88px] px-1 py-0.5">
                  <div className="flex items-center gap-0.5 justify-center">
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                    <span className={`font-medium ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{program.rating}</span>
                  </div>
                  {reviewCount > 0 && (
                    <span className={`text-[10px] leading-tight ${isDarkTheme ? 'text-slate-400' : 'text-gray-500'}`}>({reviewCount})</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className={`font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'} text-base md:text-lg line-clamp-3 flex-1 min-w-0`}>
                  {formatCampDisplayName(program)}
                </h3>
                {onSave && (
                  <button
                    type="button"
                    onClick={handleSaveClick}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                    aria-label={isSaved ? 'Unsave program' : 'Save program'}
                  >
                    <Bookmark
                      className={`w-4 h-4 ${isSaved ? 'fill-brand-600 text-brand-600' : 'text-gray-700'}`}
                    />
                  </button>
                )}
              </div>
              {getOrganizationString() && (
                <p className={`text-xs truncate mb-1 ${isDarkTheme ? 'text-slate-400' : 'text-gray-600'}`}>
                  {getOrganizationString()}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 mb-1">
                {getLocationString() && (
                  <div className="flex items-start gap-1 min-w-0 flex-1">
                    <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-2 min-w-0">{getLocationString()}</span>
                  </div>
                )}
                {(() => {
                  const ageRange = program.ageRange || program.age_range || program.program_data?.age_range || program.program_data?.ageRange || null;
                  const ageMin = program.age_min || program.program_data?.age_min;
                  const ageMax = program.age_max || program.program_data?.age_max;
                  if (ageRange && typeof ageRange === 'string' && ageRange.trim() !== '') {
                    return (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Users className="h-3 w-3" />
                        <span>{ageRange}</span>
                      </div>
                    );
                  }
                  if (ageMin != null && ageMax != null) {
                    return (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Users className="h-3 w-3" />
                        <span>Ages {ageMin}-{ageMax}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
                {(program.grade_range || program.program_data?.grade_range) && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Users className="h-3 w-3" />
                    <span>{program.grade_range || program.program_data?.grade_range}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryColor(program.category || program.primary_category || 'Program')}`}>
                  {getCategoryDisplayName(program.category || program.primary_category || 'Program')}
                </span>
                {(() => {
                  const campTypeBadge = getCampTypeBadge();
                  return campTypeBadge ? (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${campTypeBadge.className}`}>
                      {campTypeBadge.label}
                    </span>
                  ) : null;
                })()}
              </div>
              {program.distance != null && program.distance !== undefined && parseFloat(program.distance) > 0 && (
                <div className={`flex items-center gap-1 text-xs mt-1 ${isDarkTheme ? 'text-slate-400' : 'text-gray-600'}`}>
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  <span>{parseFloat(program.distance).toFixed(1)} miles away</span>
                </div>
              )}
              {getDisplayCost() && (
                <div className={`text-xs mt-1 ${isDarkTheme ? 'text-slate-400' : 'text-gray-600'}`}>
                  {getDisplayCost()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Saved/Management View - Horizontal List
  if ((viewMode === 'saved' || viewMode === 'management') && variant === 'horizontal') {
    const assignedChildren = program.assignedChildren || [];
    const maxVisibleChildren = 3; // Mobile shows max 3

    const handleCardClick = () => {
      if (onView) {
        onView(program);
      } else if (onClick) {
        onClick(program);
      }
    };

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={
          flatListing
            ? `relative flex flex-col py-4 border-b transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${isDarkTheme ? 'border-slate-700/40 hover:bg-slate-800/50' : 'border-gray-100 hover:bg-gray-50/50'} ${uniformHeight ? 'min-h-[152px]' : ''}`
            : `
          relative ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} border ${isDarkTheme ? 'border-slate-700/40' : 'border-gray-100'} rounded-lg cursor-pointer transition-all duration-300
          hover:shadow-md
          ${uniformHeight ? 'min-h-[152px] flex flex-col' : ''}
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
        `
        }
        aria-label={`View details for ${formatCampDisplayName(program)}`}
      >
        <div className={flatListing ? `px-0 ${uniformHeight ? 'flex-1 flex' : ''}` : `p-3 md:p-4 lg:p-5 ${uniformHeight ? 'flex-1 flex' : ''}`}>
          <div className={`flex items-start gap-2 md:gap-3 ${uniformHeight ? 'w-full' : ''}`}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center overflow-hidden relative">
                {photoUrl ? (
                  <>
                    {(!imageError || imageLoading) && (
                      <img 
                        src={photoUrl} 
                        alt={program.name || program.title}
                        className="w-full h-full object-cover"
                        onLoad={() => {
                          setImageLoading(false);
                          setImageError(false);
                        }}
                        onError={(e) => {
                          if (errorTimeoutRef.current) {
                            clearTimeout(errorTimeoutRef.current);
                          }
                          errorTimeoutRef.current = setTimeout(() => {
                            setImageError(true);
                            setImageLoading(false);
                            errorTimeoutRef.current = null;
                          }, 2000);
                          e.preventDefault();
                        }}
                      />
                    )}
                    <div className={`absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center ${imageError && !imageLoading ? '' : 'hidden'}`}>
                      <div className="text-xl md:text-2xl">{programEmoji}</div>
                    </div>
                  </>
                ) : (
                  <div className="text-xl md:text-2xl">{programEmoji}</div>
                )}
              </div>
              
              {/* Star Rating - below camp image */}
              {(() => {
                const rating = program.rating || program.program_data?.rating || null;
                const reviewCount = program.reviewCount || program.user_ratings_total || program.review_count || program.reviews?.length || program.program_data?.user_ratings_total || program.program_data?.reviewCount || 0;
                
                // More lenient check - also log for debugging
                const hasRating = rating !== null && rating !== undefined && rating !== '' && 
                                  (typeof rating === 'number' ? rating > 0 : parseFloat(rating) > 0);
                
                if (hasRating) {
                  return (
                    <div className="flex flex-col items-center justify-center gap-0.5 text-xs max-w-[72px] md:max-w-[88px] px-1 py-0.5">
                      <div className="flex items-center gap-0.5 justify-center">
                        <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{rating}</span>
                      </div>
                      {reviewCount > 0 && (
                        <span className="text-gray-500 text-[10px] leading-tight">({reviewCount})</span>
                      )}
                    </div>
                  );
                }
                
                // Debug: log when rating should show but doesn't
                if (program.name || program.title) {
                  console.log('⭐ Rating not showing for:', {
                    name: program.name || program.title,
                    rating,
                    ratingType: typeof rating,
                    hasRating,
                    programRating: program.rating,
                    programDataRating: program.program_data?.rating
                  });
                }
                
                return null;
              })()}
            </div>

            <div className={`flex-1 min-w-0 ${uniformHeight ? 'flex flex-col h-full' : ''}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1 flex-wrap">
                    <h3 className={`font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'} text-base md:text-lg line-clamp-3 flex-1 min-w-0`}>
                      {formatCampDisplayName(program)}
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                      {getSourceBadge()}
                      {/* Session badge moved to date row on small screens to avoid cutting off camp name when child profile is present */}
                      {/* Always show category badge in saved/management view unless explicitly hidden */}
                      {!hideCategoryBadge && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryColor(program.category || program.primary_category || 'Program')}`}>
                          {getCategoryDisplayName(program.category || program.primary_category || 'Program')}
                        </span>
                      )}
                      {/* Show camp type badge if camp_type is available */}
                      {(() => {
                        const campTypeBadge = getCampTypeBadge();
                        return campTypeBadge ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${campTypeBadge.className}`}>
                            {campTypeBadge.label}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  
                  {(() => {
                    const org = program.organization || program.vicinity || program.program_data?.organization || null;
                    if (org && org.trim().toLowerCase() !== 'unknown organization' && org.trim() !== '') {
                      return (
                        <p className="text-xs md:text-sm text-gray-600 truncate mb-1">
                          {org}
                        </p>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Location, Age Range, and Grade Range - under organization */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 mb-1">
                    {(() => {
                      const locationStr = getLocationString();
                      if (locationStr && locationStr.trim() !== '') {
                        return (
                          <div className="flex items-start gap-1 min-w-0 flex-1">
                            <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-2 min-w-0">
                              {locationStr}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {(() => {
                      const ageRange = program.ageRange || program.age_range || program.program_data?.age_range || program.program_data?.ageRange || null;
                      const ageMin = program.age_min || program.program_data?.age_min;
                      const ageMax = program.age_max || program.program_data?.age_max;
                      if (ageRange && typeof ageRange === 'string' && ageRange.trim() !== '') {
                        return (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Users className="h-3 w-3" />
                            <span>{ageRange}</span>
                          </div>
                        );
                      }
                      if (ageMin != null && ageMax != null) {
                        return (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Users className="h-3 w-3" />
                            <span>Ages {ageMin}-{ageMax}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {(program.grade_range || program.program_data?.grade_range) && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Users className="h-3 w-3" />
                        <span>{program.grade_range || program.program_data?.grade_range}</span>
                      </div>
                    )}
                    {(program.phone || program.contact_phone) && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Phone className="h-3 w-3" />
                        <a href={`tel:${program.contact_phone || program.phone}`} className="text-gray-600 hover:text-brand-600 truncate">
                          {program.contact_phone || program.phone}
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Camp Dates - Session tag next to date to free space for camp name when child profile is present */}
                  {(getDisplayDates() || program.isSession) && (
                    <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600 mt-1">
                      {getDisplayDates() && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 flex-shrink-0" />
                          <span>{getDisplayDates()}</span>
                        </div>
                      )}
                      {program.isSession && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 flex-shrink-0">
                          Session
                        </span>
                      )}
                      {getDisplayDates() && (
                        <CommunityFieldIndicator 
                          field="camp_start_date" 
                          camp={program}
                          size="xs"
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Session Times - show if available */}
                  {getDisplayTimes() && (
                    <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                      <Clock className="h-3 w-3" />
                      <span>{getDisplayTimes()}</span>
                      <CommunityFieldIndicator 
                        field="session_start_time" 
                        camp={program}
                        size="xs"
                      />
                    </div>
                  )}
                  
                  {/* Status Badges - moved below camp dates */}
                  {statusInlineWithTitle && (
                    <div className="mt-1.5">
                      {renderStatusBadges({ variant: 'subtle', wrapperClass: 'flex flex-wrap' })}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {assignedChildren.length > 0 && (
                    <div className="flex items-center gap-1.5 justify-end">
                      <div className="flex -space-x-1.5">
                        {assignedChildren
                          .filter(childName => {
                            if (!childName || typeof childName !== 'string') return false;
                            const trimmed = childName.trim();
                            return trimmed.length > 0 && trimmed.toLowerCase() !== 'child' && trimmed.toLowerCase() !== 'children';
                          })
                          .slice(0, maxVisibleChildren)
                          .map((childName, idx) => {
                            const photoUrl = findChildPhoto(childName, children);
                            return (
                              <div
                                key={idx}
                                className="w-9 h-9 md:w-10 md:h-10 p-0.5 rounded-full flex items-center justify-center"
                                style={{
                                  background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.75) 0%, rgba(13, 148, 136, 0.75) 25%, rgba(104, 94, 255, 0.75) 50%, rgba(236, 72, 153, 0.75) 75%, rgba(245, 158, 11, 0.75) 100%)'
                                }}
                                title={childName}
                              >
                                {photoUrl ? (
                                  <img
                                    src={photoUrl}
                                    alt={childName}
                                    className="w-full h-full rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const parent = e.target.parentElement;
                                      if (parent) {
                                        const fallback = document.createElement('div');
                                        const isDark = document.documentElement.classList.contains('dark');
                                        fallback.className = `w-full h-full rounded-full ${isDark ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`;
                                        fallback.innerHTML = `<span class="text-sm md:text-lg font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}">${getInitials(childName)}</span>`;
                                        parent.appendChild(fallback);
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className={`w-full h-full rounded-full ${isDarkTheme ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
                                    <span className={`text-sm md:text-lg font-semibold ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                                      {getInitials(childName)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        {assignedChildren.length > maxVisibleChildren && (
                          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-white text-sm md:text-lg font-semibold">
                            +{assignedChildren.length - maxVisibleChildren}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {getDisplayCost() && (
                    <div className="text-right flex items-center gap-1 justify-end">
                      <span>{getDisplayCost()}</span>
                      <CommunityFieldIndicator 
                        field="cost_details" 
                        camp={program}
                        size="xs"
                      />
                    </div>
                  )}
                </div>
              </div>

              {statusInlineWithTitle ? (
                <div className={`mt-1 flex flex-col gap-2 items-end ${uniformHeight ? 'mt-auto' : ''}`}>
                  {renderActionButtons('flex flex-wrap gap-2')}
                </div>
              ) : (
                <div className={`flex items-center gap-1.5 flex-wrap justify-end ${uniformHeight ? 'mt-auto' : ''}`}>
                  {renderStatusBadges()}
                  {renderActionButtons()}
                </div>
              )}

              {/* Community Sharing Indicator */}
              {program.promoted_to_audit_id && program.auto_shared_with_community && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Badge variant="outline" size="sm">
                    <Users className="w-3 h-3 mr-1" />
                    Shared with community
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Perfect Match View - Horizontal
  if (viewMode === 'perfectMatch' && variant === 'horizontal') {
    const matchScore = matchData?.score || program.matchScore || 0;
    const matchConfidence = matchData?.confidence || program.matchConfidence || 'medium';
    const matchReason = matchData?.reason || program.matchReason || '';
    const matchedChildren = matchData?.matchedChildren || [];
    // Extract reasons array, or parse from reason string if array not available
    const matchReasons = matchData?.reasons || (matchReason ? matchReason.split(' • ') : []);

    const getConfidenceBadgeColor = (confidence) => {
      switch (confidence) {
        case 'excellent match':
          return 'bg-green-100 text-green-700 border-green-200';
        case 'great match':
          return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        case 'good match':
          return 'bg-blue-100 text-blue-700 border-blue-200';
        case 'high':
          return 'bg-green-100 text-green-700 border-green-200'; // Legacy support
        case 'medium':
          return 'bg-yellow-100 text-yellow-700 border-yellow-200'; // Legacy support
        case 'low':
          return 'bg-gray-100 text-gray-700 border-gray-200';
        default:
          return 'bg-gray-100 text-gray-700 border-gray-200';
      }
    };

    // Get match label text
    const getMatchLabel = (confidence, score) => {
      if (confidence === 'excellent match' || confidence === 'great match' || confidence === 'good match') {
        return confidence.charAt(0).toUpperCase() + confidence.slice(1);
      }
      // Legacy fallback
      if (score >= 80) return 'Excellent Match';
      if (score >= 60) return 'Great Match';
      if (score >= 40) return 'Good Match';
      return `${score}% Match`;
    };

    const getScoreColor = (score) => {
      if (score >= 80) return 'text-green-600';
      if (score >= 60) return 'text-yellow-600';
      return 'text-blue-600';
    };

    const formatAgeRange = () => {
      if (program.age_min && program.age_max) return `Ages ${program.age_min}-${program.age_max}`;
      if (program.ageRange) return program.ageRange;
      return null;
    };

    const formatGradeRange = () => {
      return program.grade_range || program.program_data?.grade_range || null;
    };

    const formatDistance = () => {
      // Only show distance if it's greater than 0 (suppress 0.0 miles)
      if (program.distance_miles && parseFloat(program.distance_miles) > 0) {
        return `${parseFloat(program.distance_miles).toFixed(1)}mi away`;
      }
      if (program.distance && parseFloat(program.distance) > 0) {
        return `${parseFloat(program.distance).toFixed(1)}mi away`;
      }
      return null;
    };

    return (
      <div 
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={`relative flex h-full flex-col text-left py-4 border-b transition-colors cursor-pointer focus:outline-none ${isDarkTheme ? 'border-slate-700/40 hover:bg-slate-800/50' : 'border-gray-100 hover:bg-gray-50/50'}`}
        aria-label={`View details for ${formatCampDisplayName(program)}`}
      >
          
          <div className="flex flex-1 gap-2 min-w-0">
            <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-purple-100 to-teal-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {photoUrl ? (
                <>
                  {(!imageError || imageLoading) && (
                    <img 
                      src={photoUrl} 
                      alt={program.name || program.title}
                      className="w-full h-full object-cover rounded-xl"
                      onLoad={() => {
                        setImageLoading(false);
                        setImageError(false);
                      }}
                      onError={() => {
                        if (errorTimeoutRef.current) {
                          clearTimeout(errorTimeoutRef.current);
                        }
                        errorTimeoutRef.current = setTimeout(() => {
                          setImageError(true);
                          setImageLoading(false);
                          errorTimeoutRef.current = null;
                        }, 2000);
                      }}
                    />
                  )}
                  <div className={`absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center rounded-xl ${imageError && !imageLoading ? '' : 'hidden'}`}>
                    <div className="text-xl md:text-2xl lg:text-3xl">{programEmoji}</div>
                  </div>
                </>
              ) : (
                <div className="text-xl md:text-2xl lg:text-3xl">{programEmoji}</div>
              )}
            </div>
            
            <div className="flex-1 min-w-0 flex flex-col items-start text-left">
              <div className="flex items-start justify-between gap-2 mb-1 w-full min-w-0">
                <h4 className={`font-bold ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'} text-sm leading-tight line-clamp-3 flex-1 min-w-0`}>
                  {formatCampDisplayName(program)}
                </h4>
                {program.rating && (
                  <div className="flex items-center gap-1 text-sm flex-shrink-0">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className={`font-medium ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>{program.rating}</span>
                    {(() => {
                      const reviewCount = program.reviewCount || program.user_ratings_total || program.review_count || program.reviews?.length || 0;
                      // Only show review count if it's greater than 0 (match Search Results format)
                      return reviewCount > 0 ? (
                        <span className="text-gray-500">({reviewCount})</span>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap items-start gap-x-2 gap-y-1 text-xs text-slate-600 mb-1 w-full min-w-0 justify-start">
                <div className="flex items-start gap-1 min-w-0">
                  <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span className="line-clamp-2 min-w-0 block text-left">
                    {getLocationString()}
                  </span>
                </div>
                {formatDistance() && (
                  <>
                    <span className="text-gray-400 flex-shrink-0">•</span>
                    <span className="flex-shrink-0">{formatDistance()}</span>
                  </>
                )}
              </div>
              
              {(formatAgeRange() || formatGradeRange() || getDisplayCost()) && (
                <div className="flex items-center gap-2 text-xs text-slate-600 mb-2 flex-wrap justify-start">
                  {formatAgeRange() && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span>{formatAgeRange()}</span>
                    </div>
                  )}
                  {formatGradeRange() && (
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 flex-shrink-0" />
                      <span>{formatGradeRange()}</span>
                    </div>
                  )}
                  {(formatAgeRange() || formatGradeRange()) && getDisplayCost() && <span className="text-gray-400">•</span>}
                  {getDisplayCost() && (
                    <div className="flex items-center gap-1">
                      <span>{getDisplayCost()}</span>
                      <CommunityFieldIndicator 
                        field="cost_details" 
                        camp={program}
                        size="xs"
                      />
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-2 mb-2 flex-wrap justify-start w-full">
                <Badge className={`text-xs border-0 ${getCategoryColor(program.category || program.program_type_inferred || 'Program')}`}>
                  {getCategoryDisplayName(program.category || program.program_type_inferred || 'Program')}
                </Badge>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${getConfidenceBadgeColor(matchConfidence)}`}>
                    <span className={`font-bold ${getScoreColor(matchScore)}`}>
                      {getMatchLabel(matchConfidence, matchScore)}
                    </span>
                  </Badge>
                  {/* Child Avatars */}
                  {matchedChildren && matchedChildren.length > 0 && (
                    <div className="flex items-center gap-1 -ml-1">
                      {matchedChildren.slice(0, 3).map((child, idx) => (
                        <div key={child.childId || idx} className="relative" title={child.childName}>
                          <ChildAvatar child={child} size="sm" />
                        </div>
                      ))}
                      {matchedChildren.length > 3 && (
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center border-2 border-white">
                          <span className="text-xs font-semibold text-gray-600">+{matchedChildren.length - 3}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Why this is a match */}
              {matchReasons && matchReasons.length > 0 && (
                <div className="mb-2 px-2.5 py-1.5 bg-slate-50/50 rounded-lg border border-slate-100 w-full text-left">
                  <div className="text-xs font-semibold text-slate-700 mb-1">Why this is a match:</div>
                  <ul className="space-y-0.5">
                    {matchReasons.map((reason, idx) => (
                      <li key={idx} className="text-xs text-slate-600 flex items-start gap-1.5 text-left">
                        <span className="text-slate-400 mt-0.5 flex-shrink-0">•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Action Buttons - Bottom */}
              <div className="flex flex-wrap gap-2 mt-auto pt-2 w-full justify-start">
                {onWhosIn && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs px-2 md:px-3 py-1 h-7 whitespace-nowrap flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onWhosIn(program);
                    }}
                  >
                    Who's In?
                  </Button>
                )}
                {onDismiss && (
                  <>
                    {/* Show full text on md+ screens */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="hidden md:flex text-xs px-2 md:px-3 py-1 h-7 whitespace-nowrap flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismiss(program);
                      }}
                      title="Not interested"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Not Interested
                    </Button>
                    {/* Show icon-only on smaller screens */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="md:hidden text-xs px-2 py-1 h-7 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDismiss(program);
                      }}
                      title="Not interested"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </>
                )}
                <Button 
                  variant={isSaved ? 'default' : 'outline'}
                  size="sm" 
                  className={`text-xs px-2 md:px-3 py-1 h-7 whitespace-nowrap flex-shrink-0 ${isSaved ? 'bg-brand-600 hover:bg-brand-700 text-white' : ''}`}
                  onClick={handleSaveClick}
                >
                  <Bookmark className={`w-3 h-3 mr-1 ${isSaved ? 'fill-white text-white' : 'text-gray-600'}`} />
                  {isSaved ? 'Saved' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
    );
  }

  // Fallback to discovery vertical view
  return null;
};

export default UnifiedProgramCard;
